/**
 * govtId feature state
 *
 * All govt-ID state lives here. The five legacy govt-ID reducers (processedData /
 * images / captureProperties / common / idProperties) have been migrated into the
 * typed `reducers` below and deleted, along with their legacy action creators.
 *
 * The only `extraReducers` case left is RESET_ALL_REDUCERS — the app-wide reset
 * dispatched on session teardown, which is not govt-ID-owned.
 */
import { createSlice } from '@reduxjs/toolkit';
import { RESET_ALL_REDUCERS } from 'actions';
import { getCameraPermissionGuideUsage } from 'utils/utilities';
import { IMAGE_CROPPING_MODE } from 'features/govtId/helper/constants';

const PROCESSED_DATA_INITIAL = {
    faceMatch: null,
    result: null,
    tries: 0,
    failureTries: 0,
    autoCaptureFailureCount: 0,
    liveness: null,
    selfieErrorMsg: null,
    isAcuantProcessingID: null,
    ocrDocumentType: null,
};

const COMMON_INITIAL = {
    documentAttempts: {
        count: 0,
        uuid: null,
    },
    cameraPermissionGuide: {
        showGuide: false,
        properties: {},
    },
    //Tracked mixpanel events
    govtIdBrowserSupportTracked: null,
    cameraPermissionGuideUsage: {
        flow: null,
        verificationUUID: null,
        properties: {},
    },
    loading: false,

    imageCroppingMode: IMAGE_CROPPING_MODE.ALWAYS,
};

const ID_PROPERTIES_INITIAL = {
    cardType: 0,
    orientation: 0,
    sidesLeft: 2,
};

const CAPTURE_PROPERTIES_INITIAL = {
    image: {
        data: '',
        width: 0,
        height: 0,
        base64: '',
    },
    glare: -1,
    sharpness: -1,
};

const CONFIG_INITIAL = {
    instanceID: null,
    replaceFrontID: false,
    replaceBackID: false,
    qualityThreshold: 70,
    qualityAttemptCount: 0,
    autoCaptureEnabled: true,
};

const INSTRUCTIONS_INITIAL = {
    showInstructions: false,
    instructionType: null,
    lastUsedInstructionType: null,
};

const INITIAL_STATE = {
    processedData: { ...PROCESSED_DATA_INITIAL },
    images: [],
    common: { ...COMMON_INITIAL },
    idProperties: { ...ID_PROPERTIES_INITIAL },
    captureProperties: { ...CAPTURE_PROPERTIES_INITIAL },
    config: { ...CONFIG_INITIAL },
    instructions: { ...INSTRUCTIONS_INITIAL },
};

const govtIdSlice = createSlice({
    name: 'govtId',
    initialState: INITIAL_STATE,
    reducers: {
        setGovtIdBrowserEvent(state, action) {
            state.common.govtIdBrowserSupportTracked = action.payload;
        },

        //---------------idProperties--------------
        setCardType(state, action) {
            state.idProperties.cardType = action.payload;
        },
        setCardOrientation(state, action) {
            state.idProperties.orientation = action.payload;
        },
        decrementSidesLeft(state) {
            state.idProperties.sidesLeft -= 1;
        },
        incrementSidesLeft(state) {
            state.idProperties.sidesLeft += 1;
        },
        setSidesLeft(state, action) {
            state.idProperties.sidesLeft = action.payload;
        },
        resetIDProperties(state) {
            state.idProperties = { ...ID_PROPERTIES_INITIAL };
        },

        //---------------common:--------------
        setCameraPermissionGuide(state, action) {
            state.common.cameraPermissionGuide = { showGuide: true, properties: action.payload };
        },
        clearCameraPermissionGuide(state) {
            state.common.cameraPermissionGuide = { ...COMMON_INITIAL.cameraPermissionGuide };
        },
        cacheCameraPermissionGuideUsage(state, action) {
            state.common.cameraPermissionGuideUsage = getCameraPermissionGuideUsage(action.payload);
        },
        clearCameraPermissionGuideUsage(state) {
            state.common.cameraPermissionGuideUsage = {
                ...COMMON_INITIAL.cameraPermissionGuideUsage,
            };
        },
        setImageCroppingMode(state, action) {
            state.common.imageCroppingMode = action.payload;
        },
        setDocumentAttempt(state, action) {
            state.common.documentAttempts = action.payload;
        },
        resetDocumentAttempt(state) {
            state.common.documentAttempts = { count: 0, uuid: null };
        },
        setCommonLoading(state, action) {
            state.common.loading = action.payload;
        },

        //---------------processedData--------------
        addOcrDocumentType(state, action) {
            state.processedData.ocrDocumentType = action.payload;
        },
        setIdProcessing(state, action) {
            state.processedData.isAcuantProcessingID = action.payload;
        },
        setIdResultData(state, action) {
            state.processedData.result = action.payload;
            state.processedData.isAcuantProcessingID = false;
        },
        resetAutoCaptureFailureCount(state) {
            state.processedData.autoCaptureFailureCount = 0;
        },
        increaseTries(state) {
            state.processedData.tries += 1;
        },
        addFaceMatchData(state, action) {
            state.processedData.faceMatch = action.payload;
        },
        addFaceLivenessData(state, action) {
            state.processedData.liveness = action.payload;
            state.processedData.selfieErrorMsg = null;
        },
        updateSelfieErrorMsg(state, action) {
            state.processedData.selfieErrorMsg = action.payload;
        },
        resetProcessedData(state) {
            state.processedData = {
                ...PROCESSED_DATA_INITIAL,
                // we need this state to persist even if we are resetting the flow.
                failureTries: state.processedData.failureTries,
                autoCaptureFailureCount: state.processedData.autoCaptureFailureCount,
            };
            // resetting the flow also clears any captured images.
            state.images = [];
        },
        increaseAutoCaptureFailureCount(state) {
            state.processedData.autoCaptureFailureCount = state.processedData
                .autoCaptureFailureCount
                ? state.processedData.autoCaptureFailureCount + 1
                : 1;
        },
        updateDocumentClassName(state, action) {
            // legacy spread `{...state.result}` produced {} when result was null
            if (!state.processedData.result) state.processedData.result = {};
            state.processedData.result['Document Class Name'] = action.payload;
        },
        updateClassName(state, action) {
            if (!state.processedData.result) state.processedData.result = {};
            state.processedData.result['Class Name'] = action.payload;
        },

        //---------------images + captureProperties--------------
        setCaptured(state, action) {
            state.images.push(action.payload);
            state.captureProperties = { ...action.payload };
        },
        removeLastImage(state) {
            state.images = state.images.slice(0, -1);
        },

        //---------------config--------------
        setInstanceID(state, action) {
            state.config.instanceID = action.payload;
        },
        replaceCapturedID(state, action) {
            const { orientation, status } = action.payload;
            if (orientation === 0) {
                state.config.replaceFrontID = status;
            } else {
                state.config.replaceBackID = status;
            }
        },
        setQualityThreshold(state, action) {
            state.config.qualityThreshold = action.payload;
        },
        incrementQualityAttempt(state) {
            state.config.qualityAttemptCount += 1;
        },
        resetQualityAttempt(state) {
            state.config.qualityAttemptCount = 0;
        },
        setAutoCaptureEnabled(state, action) {
            state.config.autoCaptureEnabled = action.payload;
        },
        resetConfig(state) {
            state.config = { ...CONFIG_INITIAL };
        },

        //---------------instructions--------------
        setShowInstructions(state, action) {
            state.instructions.showInstructions = action.payload;
        },
        setInstructionType(state, action) {
            state.instructions.instructionType = action.payload;
        },
        setLastUsedInstructionType(state, action) {
            state.instructions.lastUsedInstructionType = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            /* -------- global reset -------- */
            // App-wide reset on session teardown. captureProperties is intentionally
            // NOT cleared here — it is only overwritten on the next image capture.
            .addCase(RESET_ALL_REDUCERS, (state) => {
                state.processedData = { ...PROCESSED_DATA_INITIAL };
                state.images = [];
                state.common = {
                    ...COMMON_INITIAL,
                    govtIdBrowserSupportTracked: state.common.govtIdBrowserSupportTracked,
                    cameraPermissionGuideUsage: state.common.cameraPermissionGuideUsage,
                    loading: false,
                };
                state.idProperties = { ...ID_PROPERTIES_INITIAL };
                state.config = { ...CONFIG_INITIAL };
                state.instructions = { ...INSTRUCTIONS_INITIAL };
            });
    },
});

export const {
    setGovtIdBrowserEvent,

    //---------------idProperties--------------
    setCardType,
    setCardOrientation,
    decrementSidesLeft,
    incrementSidesLeft,
    setSidesLeft,
    resetIDProperties,

    //---------------common--------------
    setCameraPermissionGuide,
    clearCameraPermissionGuide,
    cacheCameraPermissionGuideUsage,
    clearCameraPermissionGuideUsage,
    setImageCroppingMode,
    setDocumentAttempt,
    resetDocumentAttempt,
    setCommonLoading,

    //---------------processedData--------------
    addOcrDocumentType,
    setIdProcessing,
    setIdResultData,
    resetAutoCaptureFailureCount,
    increaseTries,
    addFaceMatchData,
    addFaceLivenessData,
    updateSelfieErrorMsg,
    resetProcessedData,
    increaseAutoCaptureFailureCount,
    updateDocumentClassName,
    updateClassName,

    //---------------images + captureProperties--------------
    setCaptured,
    removeLastImage,

    //---------------config--------------
    setInstanceID,
    replaceCapturedID,
    setQualityThreshold,
    incrementQualityAttempt,
    resetQualityAttempt,
    setAutoCaptureEnabled,
    resetConfig,

    //---------------instructions--------------
    setShowInstructions,
    setInstructionType,
    setLastUsedInstructionType,
} = govtIdSlice.actions;

export default govtIdSlice.reducer;
